import { createFileRoute } from "@tanstack/react-router";
import { Bell, Loader2, Plus, Power, RotateCcw, Trash2, Users, Zap } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import {
  CANAIS,
  useAtualizarGatilho,
  useCriarGatilho,
  useGatilhos,
  useRemoverGatilho,
  useRestaurarGatilhos,
  type CanalNotificacao,
  type Gatilho,
} from "@/api/notificacoes";
import { listarPerfis } from "@/api/registry";
import { TopBar } from "@/components/TopBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { can, useRole } from "@/lib/use-role";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/notificacoes")({
  component: NotificacoesPage,
});

/**
 * Configuração dos gatilhos (RF-33 a RF-35).
 *
 * A tela é a mesma do MVP; o que mudou é que agora ela configura um motor que
 * existe. Antes os vinte gatilhos viviam em `localStorage` e nada os avaliava.
 *
 * Os gatilhos `temporal.*` são varreduras diárias — o campo "dias" deles é o
 * próprio prazo avaliado pelo agendador, não uma antecedência decorativa.
 */
function NotificacoesPage() {
  const role = useRole();
  const podeEditar = can(role, "editarParametros");
  const perfis = listarPerfis().map((p) => p.nome);

  const gatilhos = useGatilhos();
  const criar = useCriarGatilho();
  const restaurar = useRestaurarGatilhos();
  const [novoEvento, setNovoEvento] = useState("");

  const itens = gatilhos.data ?? [];
  const ativos = itens.filter((g) => g.ativo).length;
  const temporais = itens.filter((g) => g.chave.startsWith("temporal.")).length;

  const adicionar = () => {
    const evento = novoEvento.trim();
    if (!evento) return;
    criar.mutate(
      { evento, acao: "", mensagem: "", canal: "APP", dias: 0, publico: [] },
      {
        onSuccess: () => {
          setNovoEvento("");
          toast.success("Gatilho criado — configure público e mensagem", {
            description: "Gatilhos próprios ainda não têm evento de origem no barramento.",
          });
        },
        onError: (e) => toast.error(mensagem(e)),
      },
    );
  };

  return (
    <>
      <TopBar
        title="Notificações automáticas"
        subtitle={`${itens.length} gatilhos · ${ativos} ativos · ${temporais} por varredura diária`}
      />

      <div className="flex-1 space-y-4 overflow-auto p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <Kpi icon={<Bell className="h-4 w-4" />} label="Gatilhos" value={itens.length} />
          <Kpi icon={<Power className="h-4 w-4" />} label="Ativos" value={ativos} tom="primario" />
          <Kpi
            icon={<Users className="h-4 w-4" />}
            label="Perfis atendidos"
            value={perfis.length}
          />
          <Kpi icon={<Zap className="h-4 w-4" />} label="Canais" value={CANAIS.length} />
        </div>

        {podeEditar && (
          <div className="flex flex-wrap items-end gap-2 rounded-xl border border-border bg-card p-3">
            <div className="min-w-64 flex-1">
              <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                Novo gatilho (evento)
              </div>
              <Input
                value={novoEvento}
                onChange={(e) => setNovoEvento(e.target.value)}
                placeholder="Ex: Cliente não aprovou V1 em 3 dias"
                className="h-8 text-xs"
              />
            </div>
            <Button size="sm" className="h-8 gap-1.5 text-xs" onClick={adicionar}>
              <Plus className="h-3.5 w-3.5" /> Adicionar
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-8 gap-1.5 text-xs"
              onClick={() =>
                restaurar.mutate(undefined, {
                  onSuccess: ({ restaurados }) =>
                    toast.success(`${restaurados} gatilhos de fábrica restaurados`, {
                      description: "Os gatilhos criados por você foram preservados.",
                    }),
                  onError: (e) => toast.error(mensagem(e)),
                })
              }
            >
              <RotateCcw className="h-3.5 w-3.5" /> Restaurar padrão
            </Button>
          </div>
        )}

        {gatilhos.isPending && (
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Carregando gatilhos…
          </div>
        )}

        <div className="space-y-3">
          {itens.map((g) => (
            <LinhaGatilho key={g.id} gatilho={g} podeEditar={podeEditar} perfis={perfis} />
          ))}
        </div>
      </div>
    </>
  );
}

const mensagem = (e: unknown) => (e instanceof Error ? e.message : "Operação não permitida");

function LinhaGatilho({
  gatilho: g,
  podeEditar,
  perfis,
}: {
  gatilho: Gatilho;
  podeEditar: boolean;
  perfis: string[];
}) {
  const atualizar = useAtualizarGatilho();
  const remover = useRemoverGatilho();

  // Texto grava no blur: um PATCH por tecla digitada seria absurdo.
  const salvar = (dados: Partial<Gatilho>) =>
    atualizar.mutate({ id: g.id, ...dados }, { onError: (e) => toast.error(mensagem(e)) });

  const temporal = g.chave.startsWith("temporal.");

  return (
    <div
      data-gatilho={g.chave}
      className={cn(
        "rounded-xl border bg-card p-3",
        g.ativo ? "border-border" : "border-dashed border-border/60 opacity-60",
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <Input
          key={`${g.id}-evento`}
          defaultValue={g.evento}
          disabled={!podeEditar}
          onBlur={(e) => e.target.value !== g.evento && salvar({ evento: e.target.value })}
          className="h-8 min-w-64 flex-1 text-xs font-medium"
        />
        <select
          value={g.canal}
          disabled={!podeEditar}
          onChange={(e) => salvar({ canal: e.target.value as CanalNotificacao })}
          className="h-8 rounded-md border border-border bg-background px-2 text-xs text-foreground"
        >
          {CANAIS.map((c) => (
            <option key={c.valor} value={c.valor}>
              {c.rotulo}
            </option>
          ))}
        </select>
        <label
          title={temporal ? "Prazo avaliado pela varredura diária" : "Antecedência ou atraso"}
          className="flex h-8 items-center gap-1 rounded-md border border-border bg-background px-2 text-[11px] text-muted-foreground"
        >
          {temporal ? "prazo" : "antecedência"}
          <input
            type="number"
            min={0}
            defaultValue={g.dias}
            disabled={!podeEditar}
            onBlur={(e) => {
              const dias = Number(e.target.value) || 0;
              if (dias !== g.dias) salvar({ dias });
            }}
            className="w-12 bg-transparent text-right text-xs text-foreground focus:outline-none"
          />
          d
        </label>
        <Button
          size="sm"
          variant={g.ativo ? "secondary" : "outline"}
          className="h-8 text-xs"
          disabled={!podeEditar}
          onClick={() => salvar({ ativo: !g.ativo })}
        >
          {g.ativo ? "Ativo" : "Inativo"}
        </Button>
        {podeEditar && !g.base && (
          <Button
            size="sm"
            variant="ghost"
            aria-label="Excluir gatilho"
            className="h-8 w-8 p-0 text-muted-foreground hover:text-health-red"
            onClick={() =>
              remover.mutate(g.id, {
                onSuccess: () => toast.success("Gatilho removido"),
                onError: (e) => toast.error(mensagem(e)),
              })
            }
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
        <Campo rotulo="Ação esperada">
          <Input
            defaultValue={g.acao}
            disabled={!podeEditar}
            onBlur={(e) => e.target.value !== g.acao && salvar({ acao: e.target.value })}
            className="h-8 text-xs"
          />
        </Campo>
        <Campo rotulo="Mensagem">
          <Input
            defaultValue={g.mensagem}
            disabled={!podeEditar}
            onBlur={(e) => e.target.value !== g.mensagem && salvar({ mensagem: e.target.value })}
            className="h-8 text-xs"
          />
        </Campo>
      </div>

      <div className="mt-2">
        <div className="mb-1 flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Público
          </span>
          <span className="text-[10px] text-muted-foreground/70">
            além de quem o fato aponta diretamente
          </span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {perfis.map((perfil) => {
            const marcado = g.publico.includes(perfil);
            return (
              <button
                key={perfil}
                type="button"
                disabled={!podeEditar}
                onClick={() =>
                  salvar({
                    publico: marcado
                      ? g.publico.filter((x) => x !== perfil)
                      : [...g.publico, perfil],
                  })
                }
                className={cn(
                  "rounded-md border px-2 py-0.5 text-[10px] font-medium transition-colors",
                  marcado
                    ? "border-primary/40 bg-primary/15 text-primary"
                    : "border-border bg-secondary/40 text-muted-foreground",
                )}
              >
                {perfil}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Campo({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">
        {rotulo}
      </div>
      {children}
    </div>
  );
}

function Kpi({
  icon,
  label,
  value,
  tom,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  tom?: "primario";
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</span>
        <div
          className={cn(
            "grid h-7 w-7 place-items-center rounded-md bg-secondary text-muted-foreground",
            tom === "primario" && "bg-primary/15 text-primary",
          )}
        >
          {icon}
        </div>
      </div>
      <div className="mt-3 text-3xl font-bold tracking-tight text-foreground">{value}</div>
    </div>
  );
}
